import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getUploadDocsMoids,
    getMoidPosLimitAmount,
    getPOMOIDwiseUploadLimit,
    savePosUploadAmountLimit,
} from '../../api/AccountsAPI/posUploadDocsLimitConfigAPI';

// ── Async Thunks ───────────────────────────────────────────────────────────────

/** MOID dropdown source */
export const fetchUploadDocsMoids = createAsyncThunk(
    'posUploadDocsLimitConfig/fetchUploadDocsMoids',
    async (_, { rejectWithValue }) => {
        try {
            const res = await getUploadDocsMoids();
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load MOID list');
        }
    }
);

/** Grid of already-configured MOID-wise limits */
export const fetchUploadLimitGrid = createAsyncThunk(
    'posUploadDocsLimitConfig/fetchUploadLimitGrid',
    async (_, { rejectWithValue }) => {
        try {
            const res = await getPOMOIDwiseUploadLimit();
            const list = res.data?.Data || [];
            return list.filter(row => row.code !== '' && row.code != null);
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load the upload limit grid');
        }
    }
);

/** Current amount configured for a selected MOID (to prefill the amount input) */
export const fetchMoidAmount = createAsyncThunk(
    'posUploadDocsLimitConfig/fetchMoidAmount',
    async (code, { rejectWithValue }) => {
        try {
            const res = await getMoidPosLimitAmount(code);
            const list = res.data?.Data || [];
            return list[0]?.value ?? '';
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load the current amount');
        }
    }
);

/** Save/update the amount limit for a MOID */
export const submitPosUploadAmountLimit = createAsyncThunk(
    'posUploadDocsLimitConfig/submitPosUploadAmountLimit',
    async ({ code, value, Createdby }, { rejectWithValue }) => {
        try {
            const res = await savePosUploadAmountLimit({ code, value, Createdby });
            const status = res.data?.Data;
            if (status && status.toLowerCase() !== 'successfull' && status.toLowerCase() !== 'success') {
                return rejectWithValue(status);
            }
            return { code, value };
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to save the upload limit');
        }
    }
);

// ── Slice ──────────────────────────────────────────────────────────────────────

const initialState = {
    moidList: [],
    gridList: [],
    currentAmount: '',
    saveResult: null,

    loading: {
        moids: false,
        grid: false,
        amount: false,
        save: false,
    },
    errors: {
        moids: null,
        grid: null,
        amount: null,
        save: null,
    },
};

const posUploadDocsLimitConfigSlice = createSlice({
    name: 'posUploadDocsLimitConfig',
    initialState,
    reducers: {
        clearCurrentAmount: (state) => { state.currentAmount = ''; state.errors.amount = null; },
        clearPosUploadSaveResult: (state) => { state.saveResult = null; state.errors.save = null; },
        resetPosUploadDocsLimitConfig: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUploadDocsMoids.pending, s => { s.loading.moids = true; s.errors.moids = null; })
            .addCase(fetchUploadDocsMoids.fulfilled, (s, a) => { s.loading.moids = false; s.moidList = a.payload; })
            .addCase(fetchUploadDocsMoids.rejected, (s, a) => { s.loading.moids = false; s.errors.moids = a.payload; });

        builder
            .addCase(fetchUploadLimitGrid.pending, s => { s.loading.grid = true; s.errors.grid = null; })
            .addCase(fetchUploadLimitGrid.fulfilled, (s, a) => { s.loading.grid = false; s.gridList = a.payload; })
            .addCase(fetchUploadLimitGrid.rejected, (s, a) => { s.loading.grid = false; s.errors.grid = a.payload; });

        builder
            .addCase(fetchMoidAmount.pending, s => { s.loading.amount = true; s.errors.amount = null; })
            .addCase(fetchMoidAmount.fulfilled, (s, a) => { s.loading.amount = false; s.currentAmount = a.payload; })
            .addCase(fetchMoidAmount.rejected, (s, a) => { s.loading.amount = false; s.errors.amount = a.payload; });

        builder
            .addCase(submitPosUploadAmountLimit.pending, s => { s.loading.save = true; s.errors.save = null; s.saveResult = null; })
            .addCase(submitPosUploadAmountLimit.fulfilled, (s, a) => { s.loading.save = false; s.saveResult = a.payload; })
            .addCase(submitPosUploadAmountLimit.rejected, (s, a) => { s.loading.save = false; s.errors.save = a.payload; });
    },
});

export const {
    clearCurrentAmount,
    clearPosUploadSaveResult,
    resetPosUploadDocsLimitConfig,
} = posUploadDocsLimitConfigSlice.actions;

// ── Selectors ──────────────────────────────────────────────────────────────────
export const selectMoidList      = s => s.posUploadDocsLimitConfig.moidList;
export const selectUploadGridList = s => s.posUploadDocsLimitConfig.gridList;
export const selectCurrentAmount = s => s.posUploadDocsLimitConfig.currentAmount;
export const selectPosUploadSaveResult = s => s.posUploadDocsLimitConfig.saveResult;

export const selectMoidsLoading  = s => s.posUploadDocsLimitConfig.loading.moids;
export const selectGridLoading   = s => s.posUploadDocsLimitConfig.loading.grid;
export const selectAmountLoading = s => s.posUploadDocsLimitConfig.loading.amount;
export const selectSaveLoading   = s => s.posUploadDocsLimitConfig.loading.save;
export const selectGridError     = s => s.posUploadDocsLimitConfig.errors.grid;
export const selectSaveError     = s => s.posUploadDocsLimitConfig.errors.save;

export default posUploadDocsLimitConfigSlice.reducer;
